import { useState } from "react";
import {
  CheckCircle,
  XCircle,
  Trash2,
  UserCheck,
  UserX,
} from "lucide-react";
import {
  Card,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@b2b/ui-components";

interface UserBulkActionsProps {
  selectedCount: number;
  onBulkApprove: () => void;
  onBulkReject: () => void;
  onBulkDelete: () => void;
  onBulkActivate: () => void;
  onBulkSuspend: () => void;
  onClearSelection: () => void;
  isLoading?: boolean;
}

export function UserBulkActions({
  selectedCount,
  onBulkApprove,
  onBulkReject,
  onBulkDelete,
  onBulkActivate,
  onBulkSuspend,
  onClearSelection,
  isLoading = false,
}: UserBulkActionsProps) {
  const [confirmAction, setConfirmAction] = useState<{
    action: string;
    handler: () => void;
  } | null>(null);

  if (selectedCount === 0) {
    return null;
  }

  const handleAction = (action: string, handler: () => void) => {
    setConfirmAction({ action, handler });
  };

  const executeAction = () => {
    if (confirmAction) {
      confirmAction.handler();
      setConfirmAction(null);
    }
  };

  const actionLabels: Record<string, string> = {
    approve: "Approve",
    reject: "Reject",
    delete: "Delete",
    activate: "Activate",
    suspend: "Suspend",
  };

  const isApproveAction = confirmAction?.action === "approve";
  const confirmTitle = isApproveAction
    ? "Confirm Approval"
    : `Confirm ${actionLabels[confirmAction?.action || ""] || "Action"}`;
  const confirmMessage = isApproveAction
    ? `Are you sure you want to approve ${selectedCount} user${selectedCount !== 1 ? "s" : ""}? This action cannot be undone.`
    : `Are you sure you want to ${confirmAction?.action} ${selectedCount} user${selectedCount !== 1 ? "s" : ""}? This action cannot be undone.`;

  return (
    <>
      <Card className="shadow-md border-orange-200 bg-orange-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">
                {selectedCount} user{selectedCount !== 1 ? "s" : ""} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                disabled={isLoading}
              >
                Clear
              </Button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction("approve", onBulkApprove)}
                disabled={isLoading}
                className="text-green-600 hover:text-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction("reject", onBulkReject)}
                disabled={isLoading}
                className="text-red-600 hover:text-red-700"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction("activate", onBulkActivate)}
                disabled={isLoading}
                className="text-green-600 hover:text-green-700"
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Activate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction("suspend", onBulkSuspend)}
                disabled={isLoading}
                className="text-orange-600 hover:text-orange-700"
              >
                <UserX className="h-4 w-4 mr-2" />
                Suspend
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction("delete", onBulkDelete)}
                disabled={isLoading}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmAction !== null}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <DialogContent
          className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-[0_10px_30px_rgba(0,0,0,0.1)]"
          showCloseButton={false}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">
              {confirmTitle}
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-6 text-gray-600">
              {confirmMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              disabled={isLoading}
              className="border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={executeAction}
              disabled={isLoading}
              className={
                confirmAction?.action === "delete" ||
                confirmAction?.action === "reject"
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-orange-500 text-white hover:bg-orange-600"
              }
            >
              {isLoading
                ? "Processing..."
                : `Confirm ${actionLabels[confirmAction?.action || ""] || ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

